// The document-overlay context itself: the state, the React context and the two
// hooks that read it.
//
// Kept apart from `DocumentDetailOverlayContext.tsx` on purpose: that module
// mounts the overlay, and the overlay pulls in the whole preview stack (pdf.js
// included). Anything that only wants to *open* a document — a star, a table row,
// a search hit — imports this module instead and stays light.
import { createContext, useContext } from 'react'
import type { DocumentFocus, DocumentOverlayTarget } from '@/features/document-overlay/focus'

export type { DocumentOverlayTarget }

/** What else the caller knows about the document it is opening. */
export type DocumentOverlayOptions = {
  /** Emphasise this face of the document among its faces. */
  faceId?: string | null
}

export type DocumentOverlayState = {
  isOpen: boolean
  documentId: string | null
  /**
   * The face the overlay opened on, its page and the identity it is compared
   * against — all null when the overlay was opened the ordinary way (slice 08).
   */
  focus: DocumentFocus
  /**
   * Open a document. A plain id — what the documents list, search and the agent
   * pass — opens it unfocused; the star passes the whole target so the overlay
   * lands on the candidate's page with its face selected and compared.
   */
  open: (target: string | DocumentOverlayTarget, options?: DocumentOverlayOptions) => void
  close: () => void
}

export const DocumentOverlayContext = createContext<DocumentOverlayState | null>(null)

/** Open / close the document detail overlay from anywhere inside the case shell. */
export function useDocumentOverlay(): DocumentOverlayState {
  const ctx = useContext(DocumentOverlayContext)
  if (!ctx) {
    throw new Error('useDocumentOverlay must be used inside <DocumentDetailOverlayProvider>')
  }
  return ctx
}

/** Soft variant — returns null when no provider is mounted. Useful for
 *  components that may render outside a case (e.g. global search). */
export function useDocumentOverlayOptional(): DocumentOverlayState | null {
  return useContext(DocumentOverlayContext)
}
